import { X, Prohibit, SpinnerBall } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import DocumentStatuses, {
  DocumentStatusItem,
} from "@/components/results/DocumentStatuses";
import { useTranslation } from "react-i18next";
import { Progress } from "@/components/ui/progress";

type FloatingDocStatusesProps = {
  visible: boolean;
  statuses: DocumentStatusItem[];
  stage?: string | null;
  progress?: { step: number; totalsteps: number; percent: number } | null;
  onClose?: () => void;
  onCancel?: () => void;
};

export function FloatingDocStatuses({
  visible,
  statuses,
  stage,
  progress,
  onClose,
  onCancel,
}: FloatingDocStatusesProps) {
  const { t } = useTranslation("common");
  const SUPPORT_WIDGET_GUTTER_PX = 88; // ~64px bubble + margins
  return (
    <div
      className={cn(
        // Mobile: anchor to left with a right gutter for support widget; Desktop: center
        "fixed bottom-4 z-[100] left-4 md:left-1/2 md:-translate-x-1/2 md:right-auto",
        "w-auto md:w-[min(92vw,48rem)]",
        "transition-all duration-200",
        visible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-4 pointer-events-none"
      )}
      style={{
        bottom: "max(env(safe-area-inset-bottom, 16px), 1rem)",
        right: `calc(max(env(safe-area-inset-right, 0px), 0px) + ${SUPPORT_WIDGET_GUTTER_PX}px)`,
      }}
    >
      <div className="relative bg-white border rounded-lg shadow-xl">
        <div className="flex items-center justify-between px-3 py-2 border-b rounded-t-lg bg-gray-50/80">
          <span className="text-sm font-medium text-foreground/80">
            {t("generation.status", { defaultValue: "Generating..." })}
          </span>
          <div className="flex items-center gap-2">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 border border-red-200 rounded bg-red-50 hover:bg-red-100"
                aria-label={t("generation.cancel", {
                  defaultValue: "Cancel generation",
                })}
              >
                <Prohibit className="w-4 h-4" />
                {t("common:cancel", { defaultValue: "Cancel" })}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center p-1 text-foreground/70 hover:text-foreground"
              aria-label={t("common:close", { defaultValue: "Close" })}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-3">
          {stage !== "done" &&
            (() => {
              const percentRaw = progress
                ? typeof progress.percent === "number"
                  ? progress.percent
                  : progress.totalsteps
                    ? (progress.step / progress.totalsteps) * 100
                    : 0
                : 0;
              const percent = Math.max(
                0,
                Math.min(100, Math.round(percentRaw))
              );
              return (
                <div className="flex flex-col gap-4 mb-3">
                  <Progress
                    value={percent}
                    aria-label={t("generation.progress", {
                      defaultValue: "Progress",
                    })}
                  />
                  <div className="flex flex-row items-center justify-center">
                    <SpinnerBall className="inline-block mr-2 animate-spin" />
                    <div className="text-xs text-foreground/70">
                      {t("generation.percentComplete", {
                        defaultValue: "{{percent}}% complete",
                        percent,
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}

          <div className="hidden md:block">
            <DocumentStatuses statuses={statuses} stage={stage} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default FloatingDocStatuses;
